import { idbGet, idbSet } from "../cache/idb.js";

const IEM_VTEC_BYPOINT_URL = "https://mesonet.agron.iastate.edu/json/vtec_events_bypoint.py";
const EVENTS_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

const SUPPORTED_EVENT_TYPES = new Set([
  "TORNADO WARNING",
  "SEVERE THUNDERSTORM WARNING",
  "FLASH FLOOD WARNING",
]);

const EVENT_TYPE_WEIGHTS = {
  "TORNADO WARNING": 3,
  "SEVERE THUNDERSTORM WARNING": 2,
  "FLASH FLOOD WARNING": 2,
};

function parseIsoDt(value) {
  if (!value) return null;
  const dt = new Date(String(value).replace("Z", "+00:00"));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseEpochish(value) {
  if (value == null) return null;
  let v = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(v) || v < 0) return null;
  if (v > 1e12) v /= 1000;
  const dt = new Date(v * 1000);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function iemEventType(row) {
  const event = String(row.event || "").trim().toUpperCase();
  if (SUPPORTED_EVENT_TYPES.has(event)) return event;
  const phenomena = String(row.phenomena || "").trim().toUpperCase();
  const significance = String(row.significance || "").trim().toUpperCase();
  if (significance && significance !== "W") return null;
  if (phenomena === "TO") return "TORNADO WARNING";
  if (phenomena === "SV") return "SEVERE THUNDERSTORM WARNING";
  if (phenomena === "FF") return "FLASH FLOOD WARNING";
  return null;
}

export function iemIssueDt(row) {
  const keys = [
    "issue", "issued", "init_issued", "effective", "onset", "updated",
    "product_issue", "utc_issue", "begints",
  ];
  for (const key of keys) {
    const v = row[key];
    const dt = typeof v === "string" ? parseIsoDt(v) : parseEpochish(v);
    if (dt) return dt;
  }
  if (typeof row.date === "string" && typeof row.time === "string") {
    return parseIsoDt(`${row.date}T${row.time}Z`);
  }
  return null;
}

export function topDaysFromIemRows(stationId, rows, topN = 12) {
  const daily = new Map();
  for (const row of rows) {
    const eventType = iemEventType(row);
    const issueDt = iemIssueDt(row);
    if (!eventType || !issueDt) continue;
    const dayKey = [
      issueDt.getUTCFullYear(),
      String(issueDt.getUTCMonth() + 1).padStart(2, "0"),
      String(issueDt.getUTCDate()).padStart(2, "0"),
    ].join("");
    let d = daily.get(dayKey);
    if (!d) {
      d = { counts: {}, timeBuckets: {}, total: 0 };
      daily.set(dayKey, d);
    }
    d.counts[eventType] = (d.counts[eventType] || 0) + 1;
    const minuteKey = `${String(issueDt.getUTCHours()).padStart(2, "0")}${String(issueDt.getUTCMinutes()).padStart(2, "0")}`;
    d.timeBuckets[minuteKey] = (d.timeBuckets[minuteKey] || 0) + 1;
    d.total += 1;
  }

  const events = [];
  for (const [dayKey, d] of daily.entries()) {
    if (!d.total) continue;
    const dominantType = Object.entries(d.counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
    const peakMinute = Object.entries(d.timeBuckets).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
    const weighted = Object.entries(d.counts).reduce(
      (sum, [t, count]) => sum + (EVENT_TYPE_WEIGHTS[t] || 1) * count,
      0,
    );
    events.push({
      station: stationId,
      date: dayKey,
      time: `${peakMinute}00`,
      event_type: dominantType,
      score: Math.round((weighted * 10 + d.total) * 100) / 100,
      warning_count: d.total,
      source_id: "iem_vtec_bypoint",
      title: `${dominantType.replace(/\w+/g, (w) => w[0] + w.slice(1).toLowerCase())} peak day`,
    });
  }
  events.sort((a, b) => b.score - a.score || b.date.localeCompare(a.date) || b.time.localeCompare(a.time));
  return events.slice(0, Math.max(1, topN));
}

export async function fetchIemPointEvents(lat, lon, sdate, edate) {
  const query = new URLSearchParams({
    lat: lat.toFixed(4),
    lon: lon.toFixed(4),
    sdate,
    edate,
    fmt: "json",
  });
  const resp = await fetch(`${IEM_VTEC_BYPOINT_URL}?${query}`);
  if (!resp.ok) throw new Error(`IEM events failed (${resp.status})`);
  const payload = await resp.json();
  if (Array.isArray(payload)) return payload.filter((p) => p && typeof p === "object");
  if (payload && typeof payload === "object") {
    for (const key of ["events", "results", "data"]) {
      if (Array.isArray(payload[key])) return payload[key].filter((p) => p && typeof p === "object");
    }
  }
  return [];
}

export async function getStationEvents(station, { limit = 12, years = 20 } = {}) {
  const cacheKey = `events:${station.id}:${years}:${limit}`;
  const cached = await idbGet(cacheKey);
  if (cached?.value && Date.now() - (cached.ts || 0) < EVENTS_CACHE_TTL_MS) {
    return cached.value;
  }
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear() - years + 1, 0, 1));
  const sdate = start.toISOString().slice(0, 10);
  const edate = now.toISOString().slice(0, 10);
  const rows = await fetchIemPointEvents(station.lat, station.lon, sdate, edate);
  const events = topDaysFromIemRows(station.id, rows, limit);
  const result = {
    station: station.id,
    count: events.length,
    years,
    events,
    message: events.length
      ? "ok"
      : `No interesting events found for ${station.id} in last ${years} years.`,
  };
  await idbSet(cacheKey, result, JSON.stringify(result).length);
  return result;
}
